import React, { type FC, useEffect, type PropsWithChildren } from 'react';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { type Middleware } from 'openapi-fetch';
import OpenAPI from '../endpoint/Openapi';
import LoginForm from './LoginForm';
import TenantSelection from './TenantSelection';
import type { components } from '../endpoint/Schema';
import useTenantStore from '../stores/TenantStore';
import { applyTenantColors } from '../utils/colorUtils';

type TenantInfoDto = components['schemas']['TenantInfoDto'];

const SELECTED_TENANT_KEY = 'selected_tenant';

const CognitoProtected: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, isLoading, getIdToken } = useCognitoAuth();
  const [selectedTenant, setSelectedTenant] =
    React.useState<TenantInfoDto | null>(null);
  const [showTenantSelection, setShowTenantSelection] = React.useState(false);
  const [middlewareReady, setMiddlewareReady] = React.useState(false);
  const { setTenantConfig } = useTenantStore();

  // Load selected tenant from localStorage
  React.useEffect(() => {
    if (isAuthenticated) {
      try {
        const storedTenant = localStorage.getItem(SELECTED_TENANT_KEY);
        if (storedTenant) {
          const tenant: TenantInfoDto = JSON.parse(storedTenant);
          setSelectedTenant(tenant);
          setTenantConfig(tenant);
          setShowTenantSelection(false); // Explicitly set to false if we have a tenant
          // Apply tenant-specific styling if available
          if (tenant.appColors) {
            applyTenantColors(tenant.appColors);
          }
        } else {
          setShowTenantSelection(true);
          setSelectedTenant(null); // Explicitly clear selected tenant
        }
      } catch (err) {
        // Error loading tenant, show selection
        setShowTenantSelection(true);
        setSelectedTenant(null);
      }
    } else {
      // Not authenticated, reset state
      setShowTenantSelection(false);
      setSelectedTenant(null);
    }
  }, [isAuthenticated, setTenantConfig]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMiddlewareReady(false);
      return;
    }

    const authMiddleware: Middleware = {
      async onRequest(req) {
        const token = getIdToken();
        if (token) {
          req.headers.set('Authorization', `Bearer ${token}`);
        }
        return req;
      },
    };

    // register middleware
    OpenAPI.use(authMiddleware);

    // Set middleware as ready after a small delay
    const timer = setTimeout(() => {
      setMiddlewareReady(true);
    }, 50);

    return () => {
      // remove middleware
      OpenAPI.eject(authMiddleware);
      clearTimeout(timer);
      setMiddlewareReady(false);
    };
  }, [isAuthenticated, getIdToken]);

  const handleTenantSelected = (tenant: TenantInfoDto) => {
    setSelectedTenant(tenant);
    setTenantConfig(tenant);
    localStorage.setItem(SELECTED_TENANT_KEY, JSON.stringify(tenant));
    setShowTenantSelection(false);

    // Apply tenant-specific styling if available
    if (tenant.appColors) {
      applyTenantColors(tenant.appColors);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onSuccess={() => {}} />;
  }

  if (!middlewareReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Setting up...</p>
        </div>
      </div>
    );
  }

  if ((showTenantSelection || !selectedTenant) && middlewareReady) {
    return <TenantSelection onTenantSelected={handleTenantSelected} />;
  }

  return <>{children}</>;
};

export default CognitoProtected;
